import React, { useState, useEffect } from "react";
import {
  CustomSlider,
  MachineryContainer,
  MachineryInformation,
  MachineryInformationWrapper,
  MachinerySubtitle,
  MachineryTileOverlay,
  MachineryTitle,
  MachineryWrapper,
  ModalBackdrop,
  ModalCard,
  ModalCloseButton,
  ModalDescription,
  ModalSlideCount,
  ModalTitle,
  TriangleText,
} from "./machinery.styles";
import { IoMdClose } from "react-icons/io";

import excavatorImage from "../../Assets/Images/optimized/IMG_3740-opt.webp";
import dozer from "../../Assets/Images/optimized/dozer-opt.webp";
import mec from "../../Assets/Images/optimized/crusher_1-opt.webp";
import screener from "../../Assets/Images/optimized/crusher-opt.webp";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import excavator1 from "../../Assets/Images/optimized/mainImage1-opt.webp";
import excavator2 from "../../Assets/Images/optimized/excavator2-opt.webp";
import excavator3 from "../../Assets/Images/optimized/excavator3-opt.webp";
import dozer2 from "../../Assets/Images/optimized/dozer2-opt.webp";
import dozer3 from "../../Assets/Images/optimized/dozer_1-opt.webp";
import dozer4 from "../../Assets/Images/optimized/dozer_2-opt.webp";
import crusher from "../../Assets/Images/optimized/crusher-opt.webp";
import excavator4 from "../../Assets/Images/optimized/excavator_1-opt.webp";
import excavator5 from "../../Assets/Images/optimized/excavator_2-opt.webp";
import excavator6 from "../../Assets/Images/optimized/excavator_3-opt.webp";
import excavator7 from "../../Assets/Images/optimized/excavator_4-opt.webp";
import excavator8 from "../../Assets/Images/optimized/excavator_5-opt.webp";
import crusher1 from "../../Assets/Images/optimized/crusher_2-opt.webp";
import crusher2 from "../../Assets/Images/optimized/crusher_4-opt.webp";
import crusher3 from "../../Assets/Images/optimized/crusher_5-opt.webp";
import crusher4 from "../../Assets/Images/optimized/crusher_6-opt.webp";

const Modal = ({ show, onClose, data }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  useEffect(() => {
    setActiveSlide(0);
  }, [show, data?.title]);

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target.id === "modal-backdrop") {
      onClose();
    }
  };
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_, next) => setActiveSlide(next),
  };

  return (
    <ModalBackdrop id="modal-backdrop" onClick={handleBackdropClick}>
      <ModalCard $isMobile={isMobile}>
        <ModalCloseButton onClick={onClose}>
          <IoMdClose />
        </ModalCloseButton>

        <ModalTitle>{data.title}</ModalTitle>
        <ModalSlideCount>
          {activeSlide + 1} / {data.images?.length || 0}
        </ModalSlideCount>
        <CustomSlider {...sliderSettings}>
          {data.images?.map((image, index) => (
            <div key={index}>
              <img src={image} alt={`${data.title} ${index + 1}`} loading={index === 0 ? "eager" : "lazy"} decoding="async" style={{ width: "100%", borderRadius: "10px" }} />
            </div>
          ))}
        </CustomSlider>
        <ModalDescription>{data.description}</ModalDescription>
      </ModalCard>
    </ModalBackdrop>
  );
};

const Machinery = () => {
  const [selectedMachinery, setSelectedMachinery] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const machineryData = {
    excavators: {
      title: "Excavators",
      images: [excavator3, excavator2, excavator1, excavatorImage, excavator4, excavator5, excavator6, excavator7, excavator8],
      description:
        "Excavators are versatile heavy machinery used for digging, trenching, material handling, demolition, and site preparation. Equipped with a powerful hydraulic system and various attachments, they are essential for construction, mining, landscaping, and infrastructure projects, enabling efficient earthmoving, lifting, and debris removal.",
    },
    screeners: {
      title: "Screeners",
      images: [screener, screener],
      description: "Screeners are machines designed to separate materials into different sizes, ensuring efficient sorting of aggregates, soil, sand, and other materials for construction and processing.",
    },
    dozers: {
      title: "Dozers",
      images: [dozer, dozer2, dozer3, dozer4],
      description: "Dozers are powerful, tracked machines designed for pushing, leveling, and grading large quantities of material. Equipped with a broad, durable blade, they excel at clearing land, shaping terrain, and handling heavy-duty earthmoving tasks.",
    },
    crusher: {
      title: "Crusher",
      images: [mec, crusher, crusher1, crusher2, crusher3, crusher4],
      description: "Crushers are powerful machines designed to break down rocks, concrete, and other materials into smaller, usable sizes. They are essential for producing aggregates used in construction, road building, and other heavy-duty applications.",
    },
  };

  const handleMachineryClick = (key) => {
    setSelectedMachinery(machineryData[key]);
    setModalOpen(true);
  };

  const machineryTiles = [
    { key: "excavators", title: "Excavators", subtitle: "Tracked and wheeled options", background: excavatorImage },
    { key: "screeners", title: "Screeners", subtitle: "Material separation and grading", background: screener },
    { key: "dozers", title: "Dozers", subtitle: "Heavy-duty earthmoving power", background: dozer },
    { key: "crusher", title: "Crusher", subtitle: "Aggregate and concrete processing", background: mec },
  ];

  return (
    <MachineryContainer>
      <MachineryTitle>Plant Machinery</MachineryTitle>
      <MachineryWrapper>
        <MachineryInformationWrapper>
          {machineryTiles.map((tile) => (
            <MachineryInformation key={tile.key} onClick={() => handleMachineryClick(tile.key)} background={tile.background}>
              <MachineryTileOverlay>
                <TriangleText>{tile.title}</TriangleText>
                <MachinerySubtitle>{tile.subtitle}</MachinerySubtitle>
              </MachineryTileOverlay>
            </MachineryInformation>
          ))}
        </MachineryInformationWrapper>
      </MachineryWrapper>

      <Modal show={modalOpen} onClose={() => setModalOpen(false)} data={selectedMachinery || {}} />
    </MachineryContainer>
  );
};

export default Machinery;
